import { Switch } from "@progress/kendo-react-inputs";
import { useInternationalization } from "@progress/kendo-react-intl";
import { Error } from "@progress/kendo-react-labels";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import quotesApi from "../../api/quotes";
import { useFetch } from "../../hooks/useApi";
import MathUtility from "../../utilities/mathUtilities";
import { LoadingIndicator } from "../LoadingIndicator";

interface IFeeValues {
  marginId: number;
  feeName: string;
  companyName: string;
  companyRole: string;
  values: Record<string, number>;
  total: number;
}

interface IMarginsDetails {
  quoteFees: IFeeValues[];
  total: number;
  minimumFee: number;
}

interface IMarginsSummaryProps {
  quoteId: number;
  pricingId: number;
  fairValue?: number;
  totalVolume?: number;
  unitPriceDecimals?: number;
  units: string;
  quote: any;
  clientLong?: boolean;
}

export const MarginsSummary = ({
  quoteId,
  quote,
  pricingId,
  fairValue,
  totalVolume = 1,
  unitPriceDecimals: decimals = 2,
  units,
  clientLong = true,
}: IMarginsSummaryProps) => {
  const formatter = useInternationalization();
  const headerStyle = { fontWeight: "bold" };
  const totalStyle = { borderTop: "double" };
  const adjustmentStyle = { fontWeight: "bold", color: "#4cbb17" };

  const { data, loading, error } = useFetch(quotesApi.getMarginsDetails, quoteId, pricingId);
  const details = data as IMarginsDetails;

  const [quoteFees, setQuoteFees] = useState<IFeeValues[]>([]);
  useEffect(() => {
    var fees =
      details?.quoteFees.map((fee) => {
        const newFee = { ...fee };
        if (!clientLong) newFee.total = -newFee.total;
        return newFee;
      }) ?? [];
    setQuoteFees(fees);
  }, [clientLong, details]);

  const totalFees = (quoteFees ?? []).reduce(
    (sum: number, current: any) => sum + MathUtility.round(current.total || 0, 2),
    0,
  );

  const totalPrice = (fairValue || 0) + totalFees;

  const { approval, submittal } = quote;

  const isInsurance = quote.productType === "Insurance";

  const showAdjustments = approval.premium !== null && !isInsurance;

  const hasBroker = submittal.brokerPremium != null;
  const hasFronter = submittal.fronterPremium != null;

  let brokerAdjustment = null,
    brokerUnitAdjustment = null,
    fronterAdjustment = null,
    fronterUnitAdjustment = null,
    riskTakerAdjustment = null,
    riskTakerUnitAdjustment = null;

  if (showAdjustments) {
    if (hasBroker && hasFronter) {
      // Client → Broker → Fronter → RT
      brokerAdjustment =
        (approval.premium - approval.fronterPremium) -
        (submittal.premium - submittal.fronterPremium);
      brokerUnitAdjustment =
        (approval.unitPrice - approval.fronterUnitPrice) -
        (submittal.unitPrice - submittal.fronterUnitPrice);
      fronterAdjustment =
        (approval.fronterPremium - approval.brokerPremium) -
        (submittal.fronterPremium - submittal.brokerPremium);
      fronterUnitAdjustment =
        (approval.fronterUnitPrice - approval.brokerUnitPrice) -
        (submittal.fronterUnitPrice - submittal.brokerUnitPrice);
      riskTakerAdjustment = approval.brokerPremium - submittal.brokerPremium;
      riskTakerUnitAdjustment = approval.brokerUnitPrice - submittal.brokerUnitPrice;
    } else if (hasBroker) {
      // Client → Broker → RT
      brokerAdjustment =
        approval.premium - approval.brokerPremium - submittal.premium + submittal.brokerPremium;
      brokerUnitAdjustment =
        approval.unitPrice - approval.brokerUnitPrice - submittal.unitPrice + submittal.brokerUnitPrice;
      riskTakerAdjustment = approval.brokerPremium - submittal.brokerPremium;
      riskTakerUnitAdjustment = approval.brokerUnitPrice - submittal.brokerUnitPrice;
    } else if (hasFronter) {
      // Client → Fronter → RT (same shape as Client → Broker → RT, fronter prices replace broker prices)
      fronterAdjustment =
        approval.premium - approval.fronterPremium - submittal.premium + submittal.fronterPremium;
      fronterUnitAdjustment =
        approval.unitPrice - approval.fronterUnitPrice - submittal.unitPrice + submittal.fronterUnitPrice;
      riskTakerAdjustment = approval.fronterPremium - submittal.fronterPremium;
      riskTakerUnitAdjustment = approval.fronterUnitPrice - submittal.fronterUnitPrice;
    } else {
      // Client → RT only
      riskTakerAdjustment = approval.premium - submittal.premium;
      riskTakerUnitAdjustment = approval.unitPrice - submittal.unitPrice;
    }

    brokerAdjustment = MathUtility.round(brokerAdjustment, 2);
    brokerUnitAdjustment = MathUtility.round(brokerUnitAdjustment, decimals);
    fronterAdjustment = MathUtility.round(fronterAdjustment, 2);
    fronterUnitAdjustment = MathUtility.round(fronterUnitAdjustment, decimals);
    riskTakerAdjustment = MathUtility.round(riskTakerAdjustment, 2);
    riskTakerUnitAdjustment = MathUtility.round(riskTakerUnitAdjustment, decimals);
  }

  const companies =
    details?.quoteFees.map((f: IFeeValues) => {
      return { name: f.companyName, role: f.companyRole, marginId: f.marginId };
    }) ?? [];

  const uniqueCompanies = Array.from(new Set(companies.map((a) => a.name)))
    .map((name) => {
      return companies.find((a) => a.name === name);
    })
    .sort((a: any, b: any) => (a.role > b.role ? -1 : 1));

  const [showUnitPrices, setShowUnitPrices] = useState(false);
  const [format, setFormat] = useState("");
  const [factor, setFactor] = useState(1);

  useEffect(() => {
    setFormat(showUnitPrices ? `n${decimals}` : "c2");
    setFactor(showUnitPrices ? totalVolume : 1);
  }, [decimals, showUnitPrices, totalVolume]);

  let rowIndex = 1;

  const renderFees = (company: any) => {
    const fees = quoteFees.filter((f) => f.companyName === company.name);
    const showMinFee =
      details.total < details.minimumFee && company.role === "RiskTaker" && isInsurance;

    return (
      <>
        {fees.map((fee, feeIndex) => (
          <>
            <GridLayoutItem col={2} row={rowIndex} key={`c2_${feeIndex + 2}`}>
              <span>{feeIndex === 0 ? fee.companyName : ""}</span>
            </GridLayoutItem>

            <GridLayoutItem col={3} row={rowIndex} key={`c3_${feeIndex + 2}`}>
              <span>{fee.feeName}</span>
            </GridLayoutItem>

            <GridLayoutItem
              col={4}
              row={rowIndex++}
              key={`c4_${feeIndex + 2}`}
              className="k-text-right"
            >
              <span>{formatter.formatNumber(fee.total / factor, format)}</span>
            </GridLayoutItem>
          </>
        ))}

        {showMinFee && (
          <>
            <GridLayoutItem col={5} row={rowIndex - 2} key={"minFeeLabel"} className="k-text-right">
              <span style={headerStyle}>Minimum Fee</span>
            </GridLayoutItem>

            <GridLayoutItem col={5} row={rowIndex - 1} key={"minFeeValue"} className="k-text-right">
              <span style={headerStyle}>{formatter.formatNumber(details.minimumFee, "c2")}</span>
            </GridLayoutItem>
          </>
        )}
      </>
    );
  };

  return (
    <>
      <LoadingIndicator loading={loading} />
      <Error>{error}</Error>

      {details && !error && (
        <GridLayout
          style={{ marginBottom: "1rem" }}
          align={{ vertical: "top" }}
          gap={{ rows: 0, cols: 5 }}
          cols={[
            { width: "10%" },
            { width: "20%" },
            { width: "25%" },
            { width: "10%" },
            { width: "25%" },
            { width: "10%" },
          ]}
        >
          <GridLayoutItem col={4} row={rowIndex++} key={"amount"} className="k-text-right">
            <span style={headerStyle}>Total</span>
            {showUnitPrices && <div style={{ fontStyle: "italic" }}>{` ($/${units})`}</div>}
          </GridLayoutItem>

          {!!fairValue && (
            <>
              <GridLayoutItem col={3} row={rowIndex} key={"fv_label"}>
                <span>Fair Value</span>
              </GridLayoutItem>
              <GridLayoutItem col={4} row={rowIndex++} key={"fv"} className="k-text-right">
                <span>{formatter.formatNumber(fairValue / factor, format)}</span>
              </GridLayoutItem>
            </>
          )}

          {uniqueCompanies.map((com, i) => (
            <>
              <GridLayoutItem col={2} row={rowIndex++} key={"h2"}>
                {com?.role === "RiskTaker" ? (
                  <Link
                    target="_blank"
                    to={`/admin/margin/${com?.marginId}`}
                    className="k-color-primary"
                  >
                    <span style={headerStyle}>{com?.role} Fees</span>
                  </Link>
                ) : (
                  <span style={headerStyle}>{com?.role} Fees</span>
                )}
              </GridLayoutItem>

              {renderFees(com)}
            </>
          ))}

          <GridLayoutItem col={3} row={rowIndex} key="total_label">
            <span style={headerStyle}>Submitted Price</span>
          </GridLayoutItem>
          <GridLayoutItem
            col={4}
            row={rowIndex}
            key={"total"}
            className="k-text-right"
            style={totalStyle}
          >
            <span>{formatter.formatNumber(totalPrice / factor, format)}</span>
          </GridLayoutItem>

          {showAdjustments && (
            <>
              {!!brokerAdjustment && (
                <>
                  <GridLayoutItem col={3} row={++rowIndex} key="bAdj_label">
                    <span style={adjustmentStyle}>Broker Fee Adjustment</span>
                  </GridLayoutItem>
                  <GridLayoutItem
                    col={4}
                    row={rowIndex}
                    key={"bAdj"}
                    className="k-text-right"
                    style={adjustmentStyle}
                  >
                    <span>
                      {formatter.formatNumber(
                        (showUnitPrices ? brokerUnitAdjustment : brokerAdjustment) as number,
                        format,
                      )}
                    </span>
                  </GridLayoutItem>
                </>
              )}
              {!!fronterAdjustment && (
                <>
                  <GridLayoutItem col={3} row={++rowIndex} key="fAdj_label">
                    <span style={adjustmentStyle}>Fronter Fee Adjustment</span>
                  </GridLayoutItem>
                  <GridLayoutItem
                    col={4}
                    row={rowIndex}
                    key={"fAdj"}
                    className="k-text-right"
                    style={adjustmentStyle}
                  >
                    <span>
                      {formatter.formatNumber(
                        (showUnitPrices ? fronterUnitAdjustment : fronterAdjustment) as number,
                        format,
                      )}
                    </span>
                  </GridLayoutItem>
                </>
              )}
              <GridLayoutItem col={3} row={++rowIndex} key="rtAdj_label">
                <span style={adjustmentStyle}>Risk Taker Adjustment</span>
              </GridLayoutItem>
              <GridLayoutItem
                col={4}
                row={rowIndex}
                key={"rtAdj"}
                className="k-text-right"
                style={adjustmentStyle}
              >
                <span>
                  {formatter.formatNumber(
                    (showUnitPrices ? riskTakerUnitAdjustment : riskTakerAdjustment) as number,
                    format,
                  )}
                </span>
              </GridLayoutItem>
              <GridLayoutItem col={3} row={++rowIndex} key="sPrice_label">
                <span style={adjustmentStyle}>Approved Price</span>
              </GridLayoutItem>
              <GridLayoutItem
                col={4}
                row={rowIndex}
                key={"sPrice"}
                className="k-text-right"
                style={{ ...adjustmentStyle, ...totalStyle }}
              >
                <span>
                  {formatter.formatNumber(
                    (showUnitPrices ? approval.unitPrice : approval.premium) as number,
                    format,
                  )}
                </span>
              </GridLayoutItem>
            </>
          )}

          <GridLayoutItem col={4} row={1} colSpan={2} key="switch">
            <div style={{ display: "flex", justifyContent: "right", paddingBottom: "1rem" }}>
              <span style={{ marginRight: 5 }}>Unit Prices</span>
              <Switch
                checked={showUnitPrices}
                onChange={() => setShowUnitPrices(!showUnitPrices)}
              />
            </div>
          </GridLayoutItem>
        </GridLayout>
      )}
    </>
  );
};
